import { Settings, Zap, Box, Cpu, Layers, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const CATEGORY_ILLUSTRATIONS = {
  vertical_machining_center:   "VMC",
  horizontal_machining_center: "HMC",
  "5_axis_machining_center":   "5-AX",
  cnc_lathe:                   "LATHE",
  cnc_lathe_with_live_tooling: "LATHE+",
  multitasking_mill_turn:      "MTLT",
  swiss_lathe:                 "SWISS",
  surface_grinder:             "SURF.G",
  cylindrical_grinder:         "CYL.G",
  wire_edm:                    "WEDM",
  sinker_edm:                  "SEDM",
  fiber_laser:                 "LASER",
  press_brake:                 "BRAKE",
  waterjet:                    "WJ",
  other:                       "CNC",
};

export default function SuggestedMachine({ suggested }) {
  const { t } = useTranslation();
  if (!suggested?.category_label) return null;
  const tag = CATEGORY_ILLUSTRATIONS[suggested.category] || "CNC";
  const env = suggested.min_work_envelope_mm || {};

  return (
    <div className="tech-card overflow-hidden" data-testid="suggested-machine">
      <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
        <div className="label-tech flex items-center gap-2">
          <Cpu size={12} className="text-blue-400"/> {t("suggestedMachine.title")}
        </div>
        <span className="text-[10px] label-tech px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">
          AI MATCH
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 p-5">
        {/* Visual block */}
        <div className="relative flex flex-col items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900 border border-slate-800 rounded-md p-5 min-h-[150px]" data-testid="machine-visual">
          <div className="absolute top-2 left-2 text-[9px] label-tech text-slate-600">CAT</div>
          <div className="font-display font-black text-3xl text-blue-400 tracking-tight">{tag}</div>
          <div className="font-mono-data text-[10px] text-slate-500 mt-1">{suggested.axes} {t("suggestedMachine.axesUpper")}</div>
          <div className="mt-3 grid grid-cols-3 gap-1 w-full">
            <div className="h-1 rounded bg-blue-500"/>
            <div className="h-1 rounded bg-blue-500/60"/>
            <div className="h-1 rounded bg-blue-500/30"/>
          </div>
        </div>

        {/* Specs */}
        <div>
          <h3 className="font-display font-bold text-xl text-slate-100">{suggested.category_label}</h3>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">{suggested.rationale}</p>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Spec icon={<Layers size={12}/>} label={t("suggestedMachine.axes")} value={suggested.axes ?? "—"}/>
            <Spec icon={<Zap size={12}/>} label="RPM" value={suggested.recommended_rpm?.toLocaleString("it-IT") ?? "—"}/>
            <Spec icon={<Settings size={12}/>} label={t("quoteTabs.spindle")} value={suggested.min_spindle_kw ? `${suggested.min_spindle_kw} kW` : "—"}/>
            <Spec icon={<Box size={12}/>} label={t("suggestedMachine.travels")} value={env.x ? `${env.x}×${env.y}×${env.z}` : "—"}/>
          </div>

          {suggested.compatible_materials?.length > 0 && (
            <div className="mt-4">
              <div className="label-tech text-[10px] mb-2">{t("suggestedMachine.compatibleMaterials")}</div>
              <div className="flex flex-wrap gap-1.5">
                {suggested.compatible_materials.map((m, i) => (
                  <span key={`mat-${m}-${i}`} className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300 font-mono-data">
                    {m.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {suggested.alternatives?.length > 0 && (
        <div className="border-t border-slate-800">
          <div className="px-5 py-2 label-tech text-[10px] bg-slate-950/50">{t("suggestedMachine.alternativesTitle")}</div>
          <div className="divide-y divide-slate-800">
            {suggested.alternatives.map((alt, i) => (
              <div key={`alt-${alt.category_label || i}`} className="px-5 py-3 flex items-start gap-3" data-testid={`machine-alt-${i}`}>
                <ArrowRight size={14} className="text-slate-500 mt-1 flex-shrink-0"/>
                <div className="flex-1">
                  <div className="font-display font-medium text-sm text-slate-200">{alt.category_label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{alt.rationale}</div>
                  {alt.trade_off && (
                    <div className="text-[11px] text-amber-300/80 mt-1">⚖ {alt.trade_off}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="px-2.5 py-2 rounded bg-slate-950 border border-slate-800">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] label-tech">{icon}{label}</div>
      <div className="font-mono-data text-slate-100 text-sm mt-0.5">{value}</div>
    </div>
  );
}
